import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Cancel,
  Warning,
  AttachMoney,
  Receipt,
  Person,
  Home,
  Business,
  CalendarToday,
  AccountBalance,
  CheckCircle,
  ArrowBack,
  Payment,
  AccountCircle,
  CreditCard,
  PhoneAndroid,
} from '@mui/icons-material';
import { hasWritePermission } from '../../utils/menuFilter';
import { useHistory, useLocation } from 'react-router-dom';
import { cancelBooking } from '../../services/project';
import { globalMasterService } from '../../services/stateAPI';

const CancleBooking = () => {
  const canWriteCancel = hasWritePermission('plot-booking.cancel-plot-booking');
  const history = useHistory();
  const location = useLocation();
  const booking = location.state?.booking || null;

  const [cancelReason, setCancelReason] = useState('');
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundMethod, setRefundMethod] = useState('');
  const [remarks, setRemarks] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cancellationChargePercent, setCancellationChargePercent] = useState('5');
  const [cancellationChargeAmount, setCancellationChargeAmount] = useState('');
  const [lastEditedField, setLastEditedField] = useState(null); // 'percent' or 'amount'
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [cancelResponse, setCancelResponse] = useState(null);
  const [apiError, setApiError] = useState('');

  // Payment method specific fields
  const [receiverName, setReceiverName] = useState('');
  const [upiId, setUpiId] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [bankOptions, setBankOptions] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [refNumber, setRefNumber] = useState('');

  // Redirect if no booking data or missing _id
  useEffect(() => {
    if (!booking) {
      history.push('/app/plotbooking/BookingDetaila');
    } else if (!booking.id) {
      console.error('Booking id is missing. Cannot proceed with cancellation.');
      setApiError('Booking ID is missing. Please go back and try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking]);

  // Initialize amount field on first load
  useEffect(() => {
    if (booking && cancellationChargePercent !== '' && lastEditedField === null && cancellationChargeAmount === '') {
      const percent = parseFloat(cancellationChargePercent) || 0;
      const calculatedCharge = (booking.paidAmount * percent) / 100;
      setCancellationChargeAmount(calculatedCharge.toFixed(2));
    }
  }, [booking, cancellationChargePercent, lastEditedField, cancellationChargeAmount]);

  // Fetch bank options from API
  useEffect(() => {
    const fetchBanks = async () => {
      setLoadingBanks(true);
      try {
        const banksData = await globalMasterService.getBanks();
        // Map to format needed for Select component: { id, value, label }
        const mappedBanks = banksData.map(bank => ({
          id: bank.id,
          value: bank.bankName,
          label: bank.bankName,
        }));
        setBankOptions(mappedBanks);
      } catch (error) {
        console.error('Failed to fetch bank options', error);
        setBankOptions([]);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchBanks();
  }, []);

  // Calculate refund amount based on cancellation charge (percentage or amount)
  useEffect(() => {
    if (booking) {
      let cancellationCharge = 0;

      if (lastEditedField === 'percent' && cancellationChargePercent !== '') {
        const percent = parseFloat(cancellationChargePercent) || 0;
        cancellationCharge = (booking.paidAmount * percent) / 100;
      } else if (lastEditedField === 'amount' && cancellationChargeAmount !== '') {
        cancellationCharge = parseFloat(cancellationChargeAmount) || 0;
      } else if (lastEditedField === null && cancellationChargePercent !== '') {
        // Initial load - use percentage
        const percent = parseFloat(cancellationChargePercent) || 0;
        cancellationCharge = (booking.paidAmount * percent) / 100;
      }

      const refund = Math.max(0, booking.paidAmount - cancellationCharge);
      // Only update refundAmount if it hasn't been manually edited (refund field)
      if (lastEditedField !== 'refund') {
        setRefundAmount(refund);
      }
    }
  }, [booking, cancellationChargePercent, cancellationChargeAmount, lastEditedField]);

  // Static refund methods
  const refundMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    // { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'rtgs_neft_imps', label: 'RTGS/NEFT/IMPS' },
    { value: 'other', label: 'Other' },
  ];

  const cancelReasons = [
    'Customer Request',
    'Payment Default',
    'Document Verification Failed',
    'Policy Violation',
    'Duplicate Booking',
    'Administrative Error',
    'Other',
  ];

  const handleCancelReasonChange = (e) => {
    setCancelReason(e.target.value);
    if (errors.cancelReason) {
      setErrors({ ...errors, cancelReason: '' });
    }
  };

  const handleRefundMethodChange = (e) => {
    const newMethod = e.target.value;
    setRefundMethod(newMethod);

    // Clear payment method specific fields when method changes
    setReceiverName('');
    setUpiId('');
    setAccountHolderName('');
    setAccountNumber('');
    setIfscCode('');
    setBankName('');
    setReceivedDate('');

    // Clear related errors
    const clearedErrors = { ...errors };
    delete clearedErrors.refundMethod;
    delete clearedErrors.receiverName;
    delete clearedErrors.upiId;
    delete clearedErrors.accountHolderName;
    delete clearedErrors.accountNumber;
    delete clearedErrors.ifscCode;
    delete clearedErrors.bankName;
    delete clearedErrors.receivedDate;

    setErrors(clearedErrors);
  };

  const handleRemarksChange = (e) => {
    setRemarks(e.target.value);
  };

  // Get required fields based on refund method
  const getRequiredFields = (method) => {
    // Receiver and date are always required; refNumber required for non-cash methods
    const base = ['receiverName', 'receivedDate'];
    const needsRefNumber = method !== 'cash' && method !== 'other';
    const baseWithRef = needsRefNumber ? ['refNumber', ...base] : base;

    switch (method) {
      case 'cash':
        return base;
      case 'upi':
        return [...baseWithRef, 'upiId'];
      case 'bank_transfer':
      case 'rtgs_neft_imps':
        return [...baseWithRef, 'accountHolderName', 'accountNumber', 'ifscCode', 'bankName'];
      case 'cheque':
        return [...baseWithRef, 'accountHolderName', 'accountNumber', 'ifscCode', 'bankName'];
      case 'other':
        return base;
      default:
        return baseWithRef;
    }
  };

  // Validate payment method specific fields
  const validatePaymentFields = () => {
    const newErrors = {};

    // Always required
    if (!receiverName.trim()) newErrors.receiverName = 'Receiver name is required';
    if (!receivedDate) newErrors.receivedDate = 'Refund date is required';

    if (refundMethod !== 'cash' && refundMethod !== 'other' && !refNumber.trim()) {
      newErrors.refNumber = 'REF Number is required';
    }

    if (refundMethod === 'upi') {
      if (!upiId.trim()) newErrors.upiId = 'UPI ID is required for UPI refund';
    } else if (['bank_transfer', 'rtgs_neft_imps'].includes(refundMethod)) {
      const label = refundMethods.find(m => m.value === refundMethod)?.label || 'Bank';
      if (!accountHolderName.trim()) newErrors.accountHolderName = `Account holder name is required for ${label} refund`;
      if (!accountNumber.trim()) newErrors.accountNumber = `Account number is required for ${label} refund`;
      if (!ifscCode.trim()) newErrors.ifscCode = `IFSC code is required for ${label} refund`;
      if (!bankName.trim()) newErrors.bankName = `Bank name is required for ${label} refund`;
    } else if (refundMethod === 'cheque') {
      if (!accountHolderName.trim()) newErrors.accountHolderName = 'Account holder name is required for cheque refund';
      if (!accountNumber.trim()) newErrors.accountNumber = 'Account number is required for cheque refund';
      if (!ifscCode.trim()) newErrors.ifscCode = 'IFSC code is required for cheque refund';
      if (!bankName.trim()) newErrors.bankName = 'Bank name is required for cheque refund';
    }

    return newErrors;
  };

  const validateForm = () => {
    const newErrors = {};
    // Cancellation reason is required
    if (!cancelReason || cancelReason.trim() === '') {
      newErrors.cancelReason = 'Cancellation reason is required';
    }
    // Refund method is required
    if (!refundMethod || refundMethod.trim() === '') {
      newErrors.refundMethod = 'Refund method is required';
    }
    // Validate refund amount
    if (refundAmount < 0) {
      newErrors.refundAmount = 'Refund amount cannot be negative';
    }
    if (refundAmount > netRefund) {
      newErrors.refundAmount = `Refund amount cannot exceed ${netRefund.toLocaleString('en-IN')}`;
    }

    // Validate payment method specific fields
    const paymentErrors = validatePaymentFields();
    Object.assign(newErrors, paymentErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirmCancel = () => {
    if (!validateForm()) {
      return;
    }
    // Open confirmation dialog
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleProceedWithCancellation = async () => {
    if (!validateForm()) {
      setConfirmDialogOpen(false);
      return;
    }

    setConfirmDialogOpen(false);
    setLoading(true);
    setApiError('');

    try {
      // Get current user info for cancelledBy
      let currentUserName = 'admin';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName = userData?.user?.name || userData?.name || userData?.username || 'admin';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Ensure we have the _id from the API response
      const bookingId = booking.id;
      if (!bookingId) {
        throw new Error('Booking ID (id) is required for cancellation');
      }

      const payload = {
        bookingId: bookingId,
        cancellationChargePercent: parseFloat(cancellationChargePercent) || 0,
        cancellationReason: cancelReason,
        refundMethod: refundMethod,
        cancelledBy: currentUserName,
        refundedAmount: parseFloat(refundAmount) || 0,
        ...(refundMethod !== 'cash' && refundMethod !== 'other' ? { refNumber: refNumber.trim() } : {}),
        receivedDate: receivedDate,
        // Payment method specific data
        ...(refundMethod === 'cash' && {
          cashRefundDetails: {
            receiverName: receiverName.trim(),
            receivedDate: receivedDate,
          }
        }),
        ...(refundMethod === 'upi' && {
          upiRefundDetails: {
            upiId: upiId.trim(),
            receiverName: receiverName.trim(),
          }
        }),
        ...(refundMethod === 'bank_transfer' && {
          bankTransferRefundDetails: {
            accountHolderName: accountHolderName.trim(),
            accountNumber: accountNumber.trim(),
            ifscCode: ifscCode.trim(),
            bankName: bankName.trim(),
          }
        }),
        ...(refundMethod === 'rtgs_neft_imps' && {
          rtgsNeftImpsRefundDetails: {
            accountHolderName: accountHolderName.trim(),
            accountNumber: accountNumber.trim(),
            ifscCode: ifscCode.trim(),
            bankName: bankName.trim(),
          }
        }),
        ...(refundMethod === 'cheque' && {
          chequeRefundDetails: {
            receiverName: receiverName.trim(),
            accountHolderName: accountHolderName.trim(),
            accountNumber: accountNumber.trim(),
            ifscCode: ifscCode.trim(),
            bankName: bankName.trim(),
          }
        }),
        ...(refundMethod === 'other' && {
          otherRefundDetails: {
            receiverName: receiverName.trim(),
            receivedDate: receivedDate,
          }
        }),
      };

      const response = await cancelBooking(payload);

      setCancelResponse(response);
      setSuccessDialogOpen(true);
      setLoading(false);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setApiError(error.message || 'Failed to cancel booking. Please try again.');
      setLoading(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    // Navigate back to booking list
    history.push('/app/booking/BookingDetail');
  };

  const handleBack = () => {
    history.push('/app/booking/BookingDetail');
  };

  if (!booking) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // Calculate cancellation charge and refund based on percentage or amount
  let cancellationCharge = 0;
  let cancellationChargePercentNum = 0;

  if (lastEditedField === 'percent' && cancellationChargePercent !== '') {
    cancellationChargePercentNum = parseFloat(cancellationChargePercent) || 0;
    cancellationCharge = (booking.paidAmount * cancellationChargePercentNum) / 100;
  } else if (lastEditedField === 'amount' && cancellationChargeAmount !== '') {
    cancellationCharge = parseFloat(cancellationChargeAmount) || 0;
    cancellationChargePercentNum = booking.paidAmount > 0 ? (cancellationCharge / booking.paidAmount) * 100 : 0;
  } else {
    // Default to percentage if neither is edited
    cancellationChargePercentNum = parseFloat(cancellationChargePercent) || 0;
    cancellationCharge = (booking.paidAmount * cancellationChargePercentNum) / 100;
  }

  const netRefund = Math.max(0, booking.paidAmount - cancellationCharge);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f9f9fb', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <IconButton onClick={handleBack} sx={{ border: '1px solid #e0e0e0' }}>
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: '#d32f2f', width: 56, height: 56 }}>
            <Cancel sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="#d32f2f">
              Cancel Booking
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {booking.bookingId} - {booking.customer}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Error Alert */}
      {apiError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setApiError('')}>
          <Typography variant="body2" fontWeight="600">
            {apiError}
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2, maxWidth: 1200, mx: 'auto' }}>
        {/* Warning Alert */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="600">
            <Warning sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
            You are about to cancel this booking. This action cannot be undone.
          </Typography>
        </Alert>

        {/* Booking Information Card */}
        <Card sx={{ mb: 3, border: '1px solid #ffebee', bgcolor: '#fff5f5' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Receipt sx={{ color: '#d32f2f', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                Booking Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Booking ID</Typography>
                  <Typography variant="body1" fontWeight="600">{booking.bookingId}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Customer</Typography>
                  <Typography variant="body1" fontWeight="600">
                    <Person sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {booking.customer}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Property Number</Typography>
                  <Typography variant="body1" fontWeight="600">
                    <Home sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {booking.plotNo}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Branch</Typography>
                  <Typography variant="body1" fontWeight="600">
                    <Business sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {booking.branchName}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Booking Date</Typography>
                  <Typography variant="body1" fontWeight="600">
                    <CalendarToday sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {booking.plotBookingDate}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Payment Plan</Typography>
                  <Chip label={booking.paymentPlan} size="small" variant="outlined" />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Financial Summary */}
        <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <AttachMoney sx={{ color: '#1976d2', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Financial Summary
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Total Property Amount</TableCell>
                    <TableCell align="right">₹{booking.plotAmount.toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Customer Paid Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      ₹{booking.paidAmount.toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#d32f2f' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography component="span" sx={{ minWidth: '140px' }}>Cancellation Charge</Typography>
                        <TextField
                          type="number"
                          value={cancellationChargePercent}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            setCancellationChargePercent(inputValue);
                            setLastEditedField('percent');

                            if (inputValue !== '' && !isNaN(parseFloat(inputValue))) {
                              const percent = parseFloat(inputValue) || 0;
                              const paidAmount = booking.paidAmount || 0;
                              const calculatedCharge = (paidAmount * percent) / 100;
                              setCancellationChargeAmount(calculatedCharge.toFixed(2));
                            } else {
                              setCancellationChargeAmount('');
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value) && value < 0) {
                              setCancellationChargePercent('0');
                            } else if (!isNaN(value) && value > 100) {
                              setCancellationChargePercent('100');
                            }
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.875rem' }}>%</InputAdornment>,
                          }}
                          inputProps={{
                            min: 0,
                            max: 100,
                            step: 0.01,
                            style: { textAlign: 'center', padding: '4px 8px' }
                          }}
                          sx={{
                            width: 120,
                            '& .MuiInputBase-root': {
                              height: 32,
                              fontSize: '0.875rem',
                            },
                            '& .MuiInputBase-input': {
                              padding: '4px 8px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                            }
                          }}
                          size="small"
                          placeholder="%"
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={cancellationChargeAmount}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          setCancellationChargeAmount(inputValue);
                          setLastEditedField('amount');

                          if (inputValue !== '' && !isNaN(parseFloat(inputValue))) {
                            const amount = parseFloat(inputValue) || 0;
                            const paidAmount = booking.paidAmount || 0;
                            const calculatedPercent = paidAmount > 0 ? (amount / paidAmount) * 100 : 0;
                            setCancellationChargePercent(calculatedPercent.toFixed(2));
                          } else {
                            setCancellationChargePercent('');
                          }
                        }}
                        onBlur={(e) => {
                          const value = parseFloat(e.target.value);
                          const paidAmount = booking.paidAmount || 0;
                          if (!isNaN(value) && value < 0) {
                            setCancellationChargeAmount('0');
                          } else if (!isNaN(value) && value > paidAmount) {
                            setCancellationChargeAmount(paidAmount.toString());
                          }
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start" sx={{ fontSize: '0.875rem', color: '#d32f2f' }}>- ₹</InputAdornment>,
                        }}
                        inputProps={{
                          min: 0,
                          max: booking.paidAmount,
                          step: 0.01,
                          style: { textAlign: 'right', padding: '4px 8px' }
                        }}
                        sx={{
                          width: 150,
                          '& .MuiInputBase-root': {
                            height: 32,
                            fontSize: '0.875rem',
                          },
                          '& .MuiInputBase-input': {
                            padding: '4px 8px',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#d32f2f',
                          }
                        }}
                        size="small"
                        placeholder="Amount"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#2e7d32' }}>Refund Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.1rem' }}>
                      ₹{netRefund.toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">Cancellation Details</Typography>
        </Divider>

        {/* Cancellation Reason */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Cancellation Reason"
              value={cancelReason}
              onChange={handleCancelReasonChange}
              error={!!errors.cancelReason}
              helperText={errors.cancelReason || 'Enter the reason for cancellation (required)'}
              required
              placeholder="Enter cancellation reason..."
            />
          </Grid>

          {/* Refund Method */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth error={!!errors.refundMethod} required>
              <InputLabel>Refund Method</InputLabel>
              <Select
                value={refundMethod}
                onChange={handleRefundMethodChange}
                label="Refund Method"
              >
                <MenuItem value="">
                  Select refund method
                </MenuItem>
                {refundMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.refundMethod && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.refundMethod}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Refund Amount (Editable) */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Refund Amount"
              type="number"
              value={refundAmount}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                  const amount = parseFloat(val) || 0;
                  if (amount <= netRefund) {
                    setRefundAmount(amount);
                    setLastEditedField('refund');
                    if (errors.refundAmount) {
                      setErrors({ ...errors, refundAmount: '' });
                    }
                  }
                }
              }}
              onBlur={(e) => {
                const value = parseFloat(e.target.value) || 0;
                if (value < 0) {
                  setRefundAmount(0);
                } else if (value > netRefund) {
                  setRefundAmount(netRefund);
                }
              }}
              error={!!errors.refundAmount}
              helperText={errors.refundAmount || `Maximum refund: ₹${netRefund.toLocaleString('en-IN')}`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountBalance sx={{ color: '#2e7d32' }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                min: 0,
                max: netRefund,
                step: 0.01,
              }}
              sx={{
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '& input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              }}
            />
          </Grid>

          {/* Outstanding Amount (Auto-calculated, Disabled) */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Outstanding Amount"
              value={Math.max(0.0, (netRefund || 0) - refundAmount).toLocaleString('en-IN')}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney sx={{ color: '#d32f2f' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Conditional fields based on refund method */}
          {refundMethod && (
            <Grid item xs={12}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                border: '1px solid #e0e0e0'
              }}>
                {refundMethod === 'cash' && <Payment sx={{ color: '#2e7d32' }} />}
                {refundMethod === 'upi' && <PhoneAndroid sx={{ color: '#1976d2' }} />}
                {refundMethod === 'bank_transfer' && <AccountBalance sx={{ color: '#ed6c02' }} />}
                {refundMethod === 'cheque' && <Receipt sx={{ color: '#9c27b0' }} />}
                {refundMethod === 'rtgs' && <AccountBalance sx={{ color: '#d32f2f' }} />}
                {refundMethod === 'neft' && <AccountBalance sx={{ color: '#0288d1' }} />}
                {refundMethod === 'imps' && <AccountBalance sx={{ color: '#7b1fa2' }} />}
                {refundMethod === 'other' && <Payment sx={{ color: '#616161' }} />}
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                  {refundMethods.find(m => m.value === refundMethod)?.label} Refund Details
                </Typography>
              </Box>
            </Grid>
          )}

          {refundMethod && (
            <>
              {refundMethod === 'upi' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="UPI ID"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    error={!!errors.upiId}
                    helperText={errors.upiId || 'UPI ID for refund transfer'}
                    required
                    placeholder="e.g., user@upi"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={refundMethod === 'cash' ? 6 : 4}>
                <TextField
                  fullWidth
                  label="Receiver Name"
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  error={!!errors.receiverName}
                  helperText={errors.receiverName || 'Name of the person receiving the refund'}
                  required
                  placeholder="Enter receiver name"
                />
              </Grid>
              {refundMethod !== 'cash' && refundMethod !== 'other' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="REF Number"
                    value={refNumber}
                    onChange={(e) => setRefNumber(e.target.value)}
                    error={!!errors.refNumber}
                    helperText={errors.refNumber || 'Reference number for refund transfer'}
                    required
                    placeholder="e.g., REF1234567890"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={refundMethod === 'cash' || refundMethod === 'other' ? 6 : 4}>
                <TextField
                  fullWidth
                  label="Received Date"
                  type="date"
                  value={receivedDate}
                  onChange={(e) => setReceivedDate(e.target.value)}
                  error={!!errors.receivedDate}
                  helperText={errors.receivedDate || 'Date when refund was processed/received'}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          {(refundMethod === 'cash') && (
            <>
            </>
          )}

          {(refundMethod === 'upi') && (
            <>
            </>
          )}

          {(refundMethod === 'bank_transfer') && (
            <>
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                  error={!!errors.accountHolderName}
                  helperText={errors.accountHolderName || 'Name of the bank account holder'}
                  required
                  placeholder="Enter account holder name"
                />
              </Grid> */}
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  error={!!errors.accountNumber}
                  helperText={errors.accountNumber || 'Bank account number'}
                  required
                  placeholder="Enter account number"
                />
              </Grid> */}
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  error={!!errors.ifscCode}
                  helperText={errors.ifscCode || 'IFSC code of the bank'}
                  required
                  placeholder="e.g., SBIN0001234"
                />
              </Grid> */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.bankName} required>
                  <InputLabel>Bank Name</InputLabel>
                  <Select
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    label="Bank Name"
                    disabled={loadingBanks}
                  >
                    <MenuItem value="">
                      {loadingBanks ? 'Loading...' : 'Select Bank'}
                    </MenuItem>
                    {bankOptions.map((bank) => (
                      <MenuItem key={bank.id} value={bank.value}>
                        {bank.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.bankName && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.bankName}
                    </Typography>
                  )}
                  {!errors.bankName && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Name of the bank
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </>
          )}

          {(refundMethod === 'rtgs_neft_imps') && (
            <>
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                  error={!!errors.accountHolderName}
                  helperText={errors.accountHolderName || `Account holder name for ${refundMethods.find(m => m.value === refundMethod)?.label}`}
                  required
                  placeholder="Enter account holder name"
                />
              </Grid> */}
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  error={!!errors.accountNumber}
                  helperText={errors.accountNumber || `Account number for ${refundMethods.find(m => m.value === refundMethod)?.label}`}
                  required
                  placeholder="Enter account number"
                />
              </Grid> */}
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  error={!!errors.ifscCode}
                  helperText={errors.ifscCode || `IFSC code for ${refundMethods.find(m => m.value === refundMethod)?.label}`}
                  required
                  placeholder="e.g., SBIN0001234"
                />
              </Grid> */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.bankName} required>
                  <InputLabel>Bank Name</InputLabel>
                  <Select
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    label="Bank Name"
                    disabled={loadingBanks}
                  >
                    <MenuItem value="">
                      {loadingBanks ? 'Loading...' : 'Select Bank'}
                    </MenuItem>
                    {bankOptions.map((bank) => (
                      <MenuItem key={bank.id} value={bank.value}>
                        {bank.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.bankName && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.bankName}
                    </Typography>
                  )}
                  {!errors.bankName && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {`Bank name for ${refundMethods.find(m => m.value === refundMethod)?.label}`}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </>
          )}

          {(refundMethod === 'cheque') && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Receiver Name"
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  error={!!errors.receiverName}
                  helperText={errors.receiverName || 'Name of the person receiving the cheque'}
                  required
                  placeholder="Enter receiver name"
                />
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                  error={!!errors.accountHolderName}
                  helperText={errors.accountHolderName || 'Name on the cheque'}
                  required
                  placeholder="Enter account holder name"
                />
              </Grid> */}
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  error={!!errors.accountNumber}
                  helperText={errors.accountNumber || 'Account number for the cheque'}
                  required
                  placeholder="Enter account number"
                />
              </Grid> */}
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  error={!!errors.ifscCode}
                  helperText={errors.ifscCode || 'IFSC code of the bank'}
                  required
                  placeholder="e.g., SBIN0001234"
                />
              </Grid> */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.bankName} required>
                  <InputLabel>Bank Name</InputLabel>
                  <Select
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    label="Bank Name"
                    disabled={loadingBanks}
                  >
                    <MenuItem value="">
                      {loadingBanks ? 'Loading...' : 'Select Bank'}
                    </MenuItem>
                    {bankOptions.map((bank) => (
                      <MenuItem key={bank.id} value={bank.value}>
                        {bank.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.bankName && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.bankName}
                    </Typography>
                  )}
                  {!errors.bankName && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Name of the bank
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </>
          )}

          {/* Remarks */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Remarks / Additional Notes"
              multiline
              rows={3}
              value={remarks}
              onChange={handleRemarksChange}
              placeholder="Enter any additional notes about this cancellation..."
            />
          </Grid>
        </Grid>

        {/* Confirmation Checkbox Section */}
        {/* <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffb74d' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
            <Warning sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
            By confirming, you agree that:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 3 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              This booking will be permanently cancelled
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              The refund amount (₹{netRefund.toLocaleString('en-IN')}) will be processed as per the selected method
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Cancellation charges (₹{cancellationCharge.toLocaleString('en-IN')}) are non-refundable
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
        </Box> */}

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid #e0e0e0', pt: 3 }}>
          <Button
            onClick={handleBack}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              borderRadius: 2
            }}
          >
            Keep Booking
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            disabled={loading || successDialogOpen || !cancelReason || cancelReason.trim() === '' || !refundMethod || refundMethod.trim() === ''}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Cancel />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
            }}
          >
            {loading ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: '#d32f2f', fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Are you sure?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to cancel booking <strong>{booking?.bookingId}</strong> for customer <strong>{booking?.customer}</strong>.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
              Cancellation Details:
            </Typography>
            {cancelReason && (
              <Typography variant="body2" color="text.secondary">
                • Cancellation Reason: {cancelReason}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              • Cancellation Charge: ₹{cancellationCharge.toLocaleString('en-IN')} ({cancellationChargePercent}%)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Refund Amount: ₹{refundAmount.toLocaleString('en-IN')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Refund Method: {refundMethod}
            </Typography>
            {refundMethod === 'cash' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  • Receiver Name: {receiverName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Received Date: {receivedDate}
                </Typography>
              </>
            )}
            {refundMethod === 'upi' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  • UPI ID: {upiId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Receiver Name: {receiverName}
                </Typography>
              </>
            )}
            {refundMethod === 'bank_transfer' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  • Account Holder: {accountHolderName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Account Number: {accountNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • IFSC Code: {ifscCode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Bank Name: {bankName}
                </Typography>
              </>
            )}
            {['rtgs', 'neft', 'imps'].includes(refundMethod) && (
              <>
                <Typography variant="body2" color="text.secondary">
                  • Account Holder: {accountHolderName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Account Number: {accountNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • IFSC Code: {ifscCode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Bank Name: {bankName}
                </Typography>
              </>
            )}
            {refundMethod === 'cheque' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  • Receiver Name: {receiverName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Account Holder: {accountHolderName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Account Number: {accountNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • IFSC Code: {ifscCode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Bank Name: {bankName}
                </Typography>
              </>
            )}
            {refundMethod === 'other' && (
              <Typography variant="body2" color="text.secondary">
                • Receiver Name: {receiverName}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 600, color: '#d32f2f' }}>
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleConfirmDialogClose}
            variant="outlined"
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            No, Keep Booking
          </Button>
          <Button
            onClick={handleProceedWithCancellation}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {loading ? 'Cancelling...' : 'Yes, Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: '#2e7d32', fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold" color="#2e7d32">
            Booking Cancelled Successfully
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Booking <strong>{cancelResponse?.data?.bookingId || booking?.bookingId}</strong> has been cancelled successfully.
            </Typography>
            {cancelResponse?.data && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
                  Cancellation Details:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Cancellation Reason: {cancelResponse.data.cancellationReason || cancelReason}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Cancellation Charge: ₹{cancelResponse.data.cancellationChargeAmount?.toLocaleString('en-IN') || cancellationCharge.toLocaleString('en-IN')} ({cancelResponse.data.cancellationChargePercent || cancellationChargePercent}%)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  • Refund Amount: ₹{cancelResponse.data.refundedAmount?.toLocaleString('en-IN') || refundAmount.toLocaleString('en-IN')}
                </Typography>
                {cancelResponse.data.refundMethod && (
                  <Typography variant="body2" color="text.secondary">
                    • Refund Method: {cancelResponse.data.refundMethod}
                  </Typography>
                )}
                {cancelResponse.data.cashRefundDetails && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • Receiver Name: {cancelResponse.data.cashRefundDetails.receiverName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Received Date: {cancelResponse.data.cashRefundDetails.receivedDate}
                    </Typography>
                  </>
                )}
                {cancelResponse.data.upiRefundDetails && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • UPI ID: {cancelResponse.data.upiRefundDetails.upiId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Receiver Name: {cancelResponse.data.upiRefundDetails.receiverName}
                    </Typography>
                  </>
                )}
                {cancelResponse.data.bankTransferRefundDetails && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • Account Holder: {cancelResponse.data.bankTransferRefundDetails.accountHolderName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Account Number: {cancelResponse.data.bankTransferRefundDetails.accountNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • IFSC Code: {cancelResponse.data.bankTransferRefundDetails.ifscCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Bank Name: {cancelResponse.data.bankTransferRefundDetails.bankName}
                    </Typography>
                  </>
                )}
                {cancelResponse.data.rtgsRefundDetails && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • Account Holder: {cancelResponse.data.rtgsRefundDetails.accountHolderName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Account Number: {cancelResponse.data.rtgsRefundDetails.accountNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • IFSC Code: {cancelResponse.data.rtgsRefundDetails.ifscCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Bank Name: {cancelResponse.data.rtgsRefundDetails.bankName}
                    </Typography>
                  </>
                )}
                {cancelResponse.data.neftRefundDetails && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • Account Holder: {cancelResponse.data.neftRefundDetails.accountHolderName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Account Number: {cancelResponse.data.neftRefundDetails.accountNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • IFSC Code: {cancelResponse.data.neftRefundDetails.ifscCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Bank Name: {cancelResponse.data.neftRefundDetails.bankName}
                    </Typography>
                  </>
                )}
                {cancelResponse.data.impsRefundDetails && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • Account Holder: {cancelResponse.data.impsRefundDetails.accountHolderName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Account Number: {cancelResponse.data.impsRefundDetails.accountNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • IFSC Code: {cancelResponse.data.impsRefundDetails.ifscCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Bank Name: {cancelResponse.data.impsRefundDetails.bankName}
                    </Typography>
                  </>
                )}
                {cancelResponse.data.chequeRefundDetails && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • Receiver Name: {cancelResponse.data.chequeRefundDetails.receiverName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Account Holder: {cancelResponse.data.chequeRefundDetails.accountHolderName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Account Number: {cancelResponse.data.chequeRefundDetails.accountNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • IFSC Code: {cancelResponse.data.chequeRefundDetails.ifscCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Bank Name: {cancelResponse.data.chequeRefundDetails.bankName}
                    </Typography>
                  </>
                )}
                {cancelResponse.data.otherRefundDetails && (
                  <Typography variant="body2" color="text.secondary">
                    • Receiver Name: {cancelResponse.data.otherRefundDetails.receiverName}
                  </Typography>
                )}
                {cancelResponse.data.cancelledAt && (
                  <Typography variant="body2" color="text.secondary">
                    • Cancelled At: {new Date(cancelResponse.data.cancelledAt).toLocaleString('en-IN')}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleSuccessDialogClose}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CancleBooking;